function isSingleChoice(question) {
  const options = question?.options;
  const answer = question?.answer == null ? null : String(question.answer);
  return (question?.type === 'mcq' || question?.type === 'listening')
    && options
    && !Array.isArray(options)
    && typeof options === 'object'
    && answer != null
    && Object.prototype.hasOwnProperty.call(options, answer)
    && String(options[answer]?.txt || '').trim();
}

function isMatching(question) {
  return question?.type === 'match'
    && Array.isArray(question.left)
    && Array.isArray(question.right)
    && question.answer
    && typeof question.answer === 'object';
}

function choiceAnswer(question) {
  return String(question.options[String(question.answer)]?.txt);
}

function listeningFromMatch(source, prompt) {
  const spoken = source.left[0];
  const rightId = source.answer[String(spoken?.id)];
  const correctIndex = source.right.findIndex(
    (item) => String(item.id) === String(rightId),
  );
  if (!spoken || source.right.length < 2 || rightId == null || correctIndex < 0) {
    return null;
  }
  return {
    id: `${source.id}_listening`,
    type: 'listening',
    category: source.category,
    question: prompt,
    sentence: null,
    options: Object.fromEntries(
      source.right.map((item, index) => [String(index + 1), { txt: String(item.text) }]),
    ),
    answer: String(correctIndex + 1),
    audioText: String(spoken.text),
    listenQuiz: true,
    speakPractice: false,
    translationPractice: false,
    audio: null,
    explanation: source.explanation,
    _generated: true,
  };
}

function listeningFromChoice(source, prompt) {
  return {
    ...source,
    id: `${source.id}_listening`,
    type: 'listening',
    question: prompt,
    sentence: null,
    audioText: choiceAnswer(source),
    listenQuiz: true,
    speakPractice: false,
    translationPractice: false,
    audio: null,
    _generated: true,
  };
}

function speakingFromChoice(source, prompt) {
  const answer = choiceAnswer(source);
  return {
    id: `${source.id}_speaking`,
    type: 'speaking',
    category: source.category,
    question: prompt,
    sentence: answer,
    expectedText: answer,
    listenQuiz: false,
    speakPractice: true,
    translationPractice: false,
    audio: null,
    explanation: source.explanation,
    _generated: true,
  };
}

function translationFromChoice(source, prompt) {
  return {
    id: `${source.id}_translation`,
    type: 'translation',
    category: source.category,
    question: `${prompt}\n${source.question}`,
    sentence: null,
    expectedText: choiceAnswer(source),
    listenQuiz: false,
    speakPractice: false,
    translationPractice: true,
    audio: null,
    explanation: source.explanation,
    _generated: true,
  };
}

export function ensureDailyTestSkillModes(
  pack,
  { listenPrompt, speakPrompt, translatePrompt },
) {
  const questions = (pack?.questions || []).map((question) => {
    if (question.type === 'listening') {
      return { ...question, question: listenPrompt };
    }
    if (question.type === 'speaking') {
      return { ...question, question: speakPrompt };
    }
    if (question.type === 'pronunciation') {
      return { ...question, question: `${listenPrompt} · ${speakPrompt}` };
    }
    if (question.type === 'translation') {
      return {
        ...question,
        question: `${translatePrompt}\n${question.question || ''}`.trim(),
      };
    }
    return { ...question };
  });
  const hasMode = (type) => questions.some((question) => (
    question.type === type
      || (type === 'listening' && question.listenQuiz === true)
      || (type === 'speaking' && question.speakPractice === true)
      || (type === 'translation' && question.translationPractice === true)
  ));
  const choiceSources = questions.filter(isSingleChoice);
  const matchingSources = questions.filter(isMatching);

  if (!hasMode('listening') && (matchingSources.length || choiceSources.length)) {
    const generated = matchingSources.length
      ? listeningFromMatch(matchingSources[0], listenPrompt)
      : listeningFromChoice(choiceSources[0], listenPrompt);
    if (generated) questions.push(generated);
  }
  if (!hasMode('speaking') && choiceSources.length) {
    questions.push(speakingFromChoice(choiceSources[0], speakPrompt));
  }
  if (!hasMode('translation') && choiceSources.length) {
    questions.push(translationFromChoice(
      choiceSources.length > 1 ? choiceSources[1] : choiceSources[0],
      translatePrompt,
    ));
  }

  return {
    ...pack,
    questions,
    numberOfQuestions: questions.length,
  };
}
